import React from "react";

import {
  Box,
  Button,
  Container,
  Divider,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Collapse,
  Snackbar,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  ListItemText,
  Checkbox,
  Tooltip,
  Link,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import { useMediaQuery } from "react-responsive";
import { useParams } from "react-router-dom";
import {
  getColorWithMode,
  getAccent,
  getBorder,
  getMutedText,
  HEADING_FONT,
} from "../../constants/colors";
import { fetchItemForItemDisplay } from "../../api/gear";
import { flagItem } from "../../api/report";
import BrandBuyButton from "./BrandBuyButton";
import ItemImage from "../ItemImage";
import PriceHistory from "./PriceHistory";
import { Helmet } from "react-helmet";
import {
  getAffiliateKey,
  getExternalUrl,
  logLinkClick,
} from "../../utils/links";
import ShareIcon from "@mui/icons-material/Share";
import MuiAlert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import GearAnalyticsContainer from "./GearAnalyticsContainer";
import ReviewSystemContainer from "../Reviews/ReviewSystemContainer";
import RateReviewIcon from "@mui/icons-material/RateReview";
import momenttz from "moment-timezone";
import GearDealsContainer from "./GearDealsContainer";
import { getUrlForCheapestItem, buildUrl } from "../../utils/gear";

const GEAR_LIST_PATHS = {
  tent: { path: "tents", label: "Tents" },
  sleepingPad: { path: "sleeping-pads", label: "Sleeping Pads" },
  sleepingBag: { path: "sleeping-bags", label: "Sleeping Bags & Quilts" },
  backpack: { path: "backpacks", label: "Backpacks" },
  insulatedJacket: { path: "insulated-jackets", label: "Insulated Jackets" },
  stove: { path: "stoves", label: "Stoves" },
};

// Product + BreadcrumbList structured data for rich search results
// (price snippets and review stars).
const buildStructuredData = (item, gearType, id) => {
  const productName = `${item.formattedBrand} ${item.name}${
    item.variant ? ` (${item.variant})` : ""
  }`;
  const retailerPrices = (item.retailers || [])
    .map(({ salePrice }) => Number(salePrice))
    .filter((price) => price > 0);
  const fallbackPrice =
    Number(item.lowestPrice) > 0
      ? Number(item.lowestPrice)
      : Number(item.price) > 0
      ? Number(item.price)
      : null;
  const prices = retailerPrices.length
    ? retailerPrices
    : fallbackPrice
    ? [fallbackPrice]
    : [];
  const rating = Number(item.runningStarRating || item.reviewValue);
  const description = (
    item.description ||
    `Specs, reviews and live pricing for the ${productName}`
  )
    .replace(/<[^>]*>/g, "")
    .slice(0, 300);

  const product = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: productName,
    brand: { "@type": "Brand", name: item.formattedBrand },
    description,
    url: buildUrl(gearType, id),
  };
  if (item.imageUrl) product.image = item.imageUrl;
  if (prices.length) {
    product.offers = {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: Math.min(...prices),
      highPrice: Math.max(...prices),
      offerCount: prices.length,
      availability: "https://schema.org/InStock",
    };
  }
  if (rating > 0 && Number(item.reviewCount) > 0) {
    product.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(rating.toFixed(1)),
      reviewCount: Number(item.reviewCount),
    };
  }

  const listInfo = GEAR_LIST_PATHS[gearType];
  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Gear",
        item: "https://www.packwizard.com/gear",
      },
      ...(listInfo
        ? [
            {
              "@type": "ListItem",
              position: 2,
              name: listInfo.label,
              item: `https://www.packwizard.com/gear/${listInfo.path}`,
            },
          ]
        : []),
      {
        "@type": "ListItem",
        position: listInfo ? 3 : 2,
        name: productName,
      },
    ],
  };

  return { product, breadcrumbs };
};

const getHeaderCellSx = (darkMode) => ({
  paddingTop: "6px",
  paddingBottom: "6px",
  fontFamily: HEADING_FONT,
  fontWeight: 600,
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: getMutedText(darkMode),
});

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const getMostRecentPriceUpdate = (retailers) => {
  if (!retailers || !retailers.length) return null;
  let mostRecentDate = 0;
  retailers.forEach(({ lastUpdated }) => {
    if (new Date(lastUpdated) > new Date(mostRecentDate)) {
      mostRecentDate = lastUpdated;
    }
  });
  return mostRecentDate;
};

const ItemDisplay = ({ darkMode, gearType, gearText }) => {
  const { id } = useParams();

  const [item, setItem] = React.useState({});
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 1380 });
  const isMobile = useMediaQuery({ maxWidth: 898 });
  const [expandDescription, setExpandDescription] = React.useState(false);
  const [descriptionOverflows, setDescriptionOverflows] = React.useState(false);
  const descriptionRef = React.useRef(null);

  // Only offer expand/collapse when the description actually overflows the
  // collapsed height.
  React.useEffect(() => {
    if (descriptionRef.current) {
      setDescriptionOverflows(descriptionRef.current.scrollHeight > 110);
    }
  }, [item]);
  const [snackbarIsOpen, setSnackbarIsOpen] = React.useState(false);
  const [reportSnackBarIsOpen, setReportSnackBarIsOpen] = React.useState(false);
  const [reportIsOpen, setReportIsOpen] = React.useState(false);
  const [incorrectSpecs, setIncorrectSpecs] = React.useState([]);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    setIncorrectSpecs(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const handleReportSnackBarClose = () => {
    setReportSnackBarIsOpen(false);
  };

  const handleSubmitReport = (itemId, incorrectSpecs) => {
    flagItem(itemId, gearType, incorrectSpecs);
    setReportIsOpen(false);
    setReportSnackBarIsOpen(true);
  };

  const getUrl = (url) => {
    const rawUrl = new URL(url);
    return rawUrl.hostname.split(/\./).slice(-2).join(".");
  };

  const handleSnackbarClose = () => {
    setSnackbarIsOpen(!snackbarIsOpen);
  };

  React.useEffect(() => {
    (async () => {
      const fetchedItem = await fetchItemForItemDisplay(gearType, id);
      setItem(fetchedItem);
    })();
  }, [id, gearType]);

  const handleCopyToClipBoard = () => {
    navigator.clipboard.writeText(window.location.href);
    setSnackbarIsOpen(true);
  };

  const handleJumpToReview = () => {
    const element = document.getElementById("review-system-container");
    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  };

  const getPrice = (price, lowestPrice) => {
    if (lowestPrice > 0) {
      return lowestPrice.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    }
    return price > 0 || !lowestPrice
      ? price.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })
      : lowestPrice.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        });
  };

  if (Object.keys(item).length === 0) return <></>;
  const specKeys = item.techSpecs || [].map(({ label }) => label);
  const urlToUseForRetailer = getUrlForCheapestItem(item.retailers, item.url);
  const mostRecentDate = getMostRecentPriceUpdate(item.retailers);

  return (
    <>
      <Stack sx={{ width: "100%", height: "100%" }}>
        <Helmet>
          <title>{`${item.formattedBrand} ${item.name} – Specs, Reviews & Best Price | PackWizard`}</title>
          <link rel="canonical" href={`${buildUrl(gearType, id)}`} />

          <meta
            name="keywords"
            content={`${item.brand} ${item.name}, ${item.brand} ${item.name} specs, ${item.brand} ${item.name} weight, ${item.brand} ${item.name} review, ${item.brand} ${item.name} cheapest`}
          />
          <meta
            name="description"
            content={`${item.formattedBrand} ${item.name}: full specs, user reviews and live price tracking${
              Number(item.lowestPrice) > 0
                ? ` — from $${Number(item.lowestPrice).toFixed(0)}`
                : ""
            } across top outdoor retailers.`}
          />
          <meta property="og:type" content="product" />
          <meta property="og:url" content={`${buildUrl(gearType, id)}`} />
          <meta
            property="og:title"
            content={`${item.formattedBrand} ${item.name}`}
          />
          <meta
            property="og:description"
            content={`Up to date weight, specs and live price tracking for retailers for the ${item.formattedBrand} ${item.name}`}
          />
          <meta
            property="og:image"
            content={item.imageUrl || "/pack_wizard.png"}
          />
          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content={`${item.formattedBrand} ${item.name}`}
          />
          <meta
            name="twitter:description"
            content={`Up to date weight, specs and live price tracking for retailers for the ${item.formattedBrand} ${item.name}`}
          />
          <meta
            name="twitter:image"
            content={item.imageUrl || `/pack_wizard.png`}
          />
          {item.name && (
            <script type="application/ld+json">
              {JSON.stringify(buildStructuredData(item, gearType, id).product)}
            </script>
          )}
          {item.name && (
            <script type="application/ld+json">
              {JSON.stringify(
                buildStructuredData(item, gearType, id).breadcrumbs
              )}
            </script>
          )}
        </Helmet>
        <Dialog
          open={reportIsOpen}
          onClose={() => {
            setReportIsOpen(false);
          }}
        >
          <DialogTitle>Report Incorrect Data</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontSize: isMobile ? "20px" : "16px" }}>
              Noticed some incorrect data?
            </DialogContentText>
            <DialogContentText
              sx={{
                maxWidth: "400px",
                fontSize: isMobile ? "18px" : "14px",
                marginTop: "5px",
              }}
            >
              With thousands of items in our database, sometimes some specs slip
              through the cracks. Please let us know which spec is incorrect and
              we'll verify and fix it ASAP. Thank you!
            </DialogContentText>
            <FormControl sx={{ m: 1, width: 300, marginTop: "10px" }}>
              <InputLabel>Incorrect Specs (Required)</InputLabel>
              <Select
                multiple
                value={incorrectSpecs}
                required
                onChange={handleChange}
                renderValue={(selected) => selected.join(", ")}
                MenuProps={MenuProps}
                label="Incorrect Specs (Required)"
              >
                {specKeys.map(({ label }) => (
                  <MenuItem key={label} value={label}>
                    <Checkbox checked={incorrectSpecs.indexOf(label) > -1} />
                    <ListItemText primary={label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setReportIsOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleSubmitReport(item._id, incorrectSpecs);
              }}
              disabled={!incorrectSpecs.length}
            >
              Report Issue
            </Button>
          </DialogActions>
        </Dialog>
        <Container
          sx={{
            width: "100%",
            height: "100%",
            textAlign: "center",
            background: darkMode
              ? "radial-gradient(ellipse 90% 120% at 50% -20%, rgba(25,118,210,0.22), transparent)"
              : "radial-gradient(ellipse 90% 120% at 50% -20%, rgba(25,118,210,0.09), transparent)",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ width: "100%", paddingTop: "10px" }}
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              disableElevation
              size="large"
              sx={{
                width: "110px",
                borderRadius: "9px",
                fontFamily: HEADING_FONT,
                fontWeight: 600,
                textTransform: "none",
              }}
              startIcon={<ShareIcon />}
              onClick={handleCopyToClipBoard}
            >
              Share
            </Button>
            <Button
              variant="contained"
              disableElevation
              size="large"
              sx={{
                width: "110px",
                color: "white",
                borderRadius: "9px",
                fontFamily: HEADING_FONT,
                fontWeight: 600,
                textTransform: "none",
              }}
              color="success"
              startIcon={<RateReviewIcon />}
              onClick={handleJumpToReview}
            >
              Review
            </Button>
          </Stack>
          <Stack
            direction="row"
            justifyContent={"flex-start"}
            sx={{ paddingBottom: "20px" }}
            spacing={1}
          >
            <Box
              sx={{
                marginTop: "10px",
                textAlign: "center",
                backgroundColor: "white",
                width: "300px",
                minWidth: "300px",
                height: "300px",
                overflow: "hidden",
                position: "relative",
                borderRadius: "12px",
                border: `1px solid ${getBorder(darkMode)}`,
              }}
            >
              <Link
                target="_blank"
                href={getExternalUrl(urlToUseForRetailer)}
                onClick={() => {
                  logLinkClick(urlToUseForRetailer);
                }}
              >
                <ItemImage
                  style={{
                    borderRadius: "5px",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    maxHeight: 300,
                    maxWidth: 300,
                    objectFit: "cover",
                    "&:hover": {
                      cursor: "pointer",
                      opacity: "0.8",
                    },
                  }}
                  alt={`${item.brand} ${item.name}`}
                  src={item.imageUrl}
                  iconSize={48}
                />
              </Link>
            </Box>
            <Stack sx={{ paddingTop: "15px" }}>
              <Typography
                variant="h4"
                component="h1"
                textAlign="left"
                sx={{
                  fontWeight: 700,
                  fontFamily: HEADING_FONT,
                  letterSpacing: "-0.01em",
                  color: getColorWithMode(darkMode, "text"),
                }}
              >
                {`${item.formattedBrand} ${item.name}`}
              </Typography>
              <Typography
                variant="h6"
                textAlign="left"
                sx={{
                  fontWeight: 700,
                  fontFamily: HEADING_FONT,
                  letterSpacing: "-0.01em",
                  color: getColorWithMode(darkMode, "text"),
                }}
              >
                {item.variant ? `(${item.variant})` : ""}
              </Typography>
              <Rating
                readOnly
                value={item.runningStarRating || item.reviewValue}
                sx={{ paddingTop: "10px" }}
              />
              <Typography
                textAlign="left"
                sx={{
                  fontSize: isMobile ? "24px" : "18px",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  fontFamily: HEADING_FONT,
                  fontWeight: 700,
                  color: getColorWithMode(darkMode, "text"),
                }}
              >
                {getPrice(item.price, item.lowestPrice)}
              </Typography>
              <Box sx={{ maxWidth: "900px" }}>
                {item.description && item.description.length > 0 ? (
                  <>
                    <Collapse
                      collapsedSize={"100px"}
                      in={expandDescription || !descriptionOverflows}
                    >
                      <Typography
                        ref={descriptionRef}
                        variant={isDesktopOrLaptop ? "body1" : "h6"}
                        sx={{
                          padding: "5px",
                          fontFamily: HEADING_FONT,
                          color: getMutedText(darkMode),
                        }}
                        textAlign="left"
                      >
                        {item.description}
                      </Typography>
                    </Collapse>
                    {descriptionOverflows && (
                      <Button
                        align="left"
                        sx={{
                          color: getAccent(darkMode),
                          fontFamily: HEADING_FONT,
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: "9px",
                        }}
                        onClick={() => {
                          setExpandDescription(!expandDescription);
                        }}
                      >
                        {expandDescription
                          ? "Collapse Description"
                          : "Expand Description"}
                      </Button>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </Box>
            </Stack>
          </Stack>
        </Container>
        <Stack spacing={1}>
          <Stack direction={isDesktopOrLaptop ? "row" : "column"}>
            <Container
              sx={{
                width: isDesktopOrLaptop ? "50%" : "100%",
                minWidth: "500px",
              }}
            >
              <Typography
                variant={isDesktopOrLaptop ? "h6" : "h5"}
                sx={{
                  height: "20px",
                  paddingLeft: "5px",
                  marginTop: "20px",
                  fontFamily: HEADING_FONT,
                  fontWeight: 600,
                }}
              >
                Specs
              </Typography>
              <Divider sx={{ paddingTop: "15px" }} />
              <Box textAlign="right">
                <Button
                  size="large"
                  sx={{ width: "120px" }}
                  startIcon={<FlagIcon />}
                  onClick={() => {
                    setReportIsOpen(true);
                  }}
                >
                  Report
                </Button>
              </Box>
              {gearType === "insulatedJacket" ? (
                <Typography
                  fontStyle="italic"
                  variant="subtitle2"
                  color="grey"
                >{`*The following specs are for the ${item.specSize} variant`}</Typography>
              ) : (
                <></>
              )}
              <TableContainer
                sx={{
                  marginTop: "5px",
                  borderRadius: "10px",
                  border: `1px solid ${getBorder(darkMode)}`,
                  backgroundImage: "none",
                  overflow: "hidden",
                }}
                component={Paper}
                elevation={0}
              >
                <Table>
                  <TableBody>
                    {item.techSpecs && item.techSpecs.length ? (
                      item.techSpecs.map(({ label, value }) =>
                        value ? (
                          <TableRow key={label}>
                            <TableCell
                              sx={{
                                fontSize: isDesktopOrLaptop ? "14px" : "18px",
                                fontFamily: HEADING_FONT,
                              }}
                            >
                              {label}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: isDesktopOrLaptop ? "14px" : "18px",
                                fontFamily: HEADING_FONT,
                              }}
                            >
                              {value}
                            </TableCell>
                          </TableRow>
                        ) : (
                          <></>
                        )
                      )
                    ) : (
                      <TableRow>
                        <TableCell align="center">No Specs Available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Container>
            {item.retailers.length === 0 ? (
              <Container
                sx={{
                  width: isDesktopOrLaptop ? "50%" : "100%",
                  minWidth: "500px",
                }}
              >
                <Stack spacing={2}>
                  <Typography
                    variant={isDesktopOrLaptop ? "h6" : "h5"}
                    sx={{
                      height: "20px",
                      paddingLeft: "5px",
                      marginTop: "20px",
                      fontFamily: HEADING_FONT,
                      fontWeight: 600,
                    }}
                  >
                    Retailers
                  </Typography>
                  <Divider />
                  {getAffiliateKey(item.url) ? (
                    <>
                      <TableContainer
                        sx={{
                          marginTop: "15px",
                          fontSize: isDesktopOrLaptop ? "16px" : "24px",
                          borderRadius: "10px",
                          border: `1px solid ${getBorder(darkMode)}`,
                          backgroundImage: "none",
                          overflow: "hidden",
                        }}
                        component={Paper}
                        elevation={0}
                      >
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                align="center"
                                sx={getHeaderCellSx(darkMode)}
                              >
                                Retailer
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={getHeaderCellSx(darkMode)}
                              >
                                Shipping
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={getHeaderCellSx(darkMode)}
                              >
                                Price
                              </TableCell>
                              <TableCell
                                sx={getHeaderCellSx(darkMode)}
                              ></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell align="center">
                                <BrandBuyButton
                                  retailer={getAffiliateKey(item.url)}
                                  url={item.url}
                                  darkMode={darkMode}
                                />
                              </TableCell>
                              <TableCell align="center">-</TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  fontSize: isDesktopOrLaptop ? "16px" : "20px",
                                  fontFamily: HEADING_FONT,
                                  fontWeight: 600,
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                <Tooltip title="This price may be out of date as we do not capture live pricing data for this item for this retailer">
                                  <Typography>
                                    {Number(item.price) ? (
                                      <Link
                                        target="_blank"
                                        href={getExternalUrl(item.url)}
                                        onClick={() => logLinkClick(item.url)}
                                        sx={{
                                          textDecoration: "none",
                                          color: "orange",
                                          "&:hover": {
                                            cursor: "pointer",
                                          },
                                        }}
                                      >
                                        {Number(item.price).toLocaleString(
                                          "en-US",
                                          {
                                            style: "currency",
                                            currency: "USD",
                                          }
                                        )}
                                      </Link>
                                    ) : (
                                      "-"
                                    )}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Link
                                  target="_blank"
                                  component={Button}
                                  variant="contained"
                                  size="large"
                                  style={{
                                    backgroundColor: "#66bb6a",
                                    color: "white",
                                    borderRadius: "9px",
                                    fontFamily: HEADING_FONT,
                                    fontWeight: 600,
                                    textTransform: "none",
}}
                                  href={getExternalUrl(item.url)}
                                  onClick={() => logLinkClick(item.url)}
                                >
                                  Buy
                                </Link>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Typography
                        variant={isDesktopOrLaptop ? "h6" : "h5"}
                        sx={{
                          height: "20px",
                          paddingLeft: "5px",
                          marginTop: "20px",
                          fontFamily: HEADING_FONT,
                          fontWeight: 600,
                        }}
                      >
                        Price History
                      </Typography>
                      <Divider sx={{ paddingTop: "15px" }} />
                      {item.retailers.length > 0 ? (
                        <PriceHistory
                          retailers={item.retailers}
                          darkMode={darkMode}
                        />
                      ) : (
                        <Typography
                          textAlign="center"
                          sx={{ marginTop: "10px" }}
                        >
                          No Pricing History Available
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        textAlign: "center",
                        height: "300px",
                        borderRadius: "12px",
                        backgroundImage: "none",
                      }}
                      component={Stack}
                      direction="column"
                      justifyContent="center"
                    >
                      <Stack
                        alignItems="center"
                        sx={{ marginTop: "-20px" }}
                        spacing={2}
                      >
                        <Typography
                          sx={{
                            fontSize: isMobile ? "24px" : "17px",
                            fontFamily: HEADING_FONT,
                            color: getMutedText(darkMode),
                          }}
                        >{`We don't yet offer live pricing for this ${gearText}`}</Typography>
                        <Link
                          target="_blank"
                          variant="contained"
                          component={Button}
                          sx={{
                            width: "300px",
                            borderRadius: "9px",
                            fontFamily: HEADING_FONT,
                            fontWeight: 600,
                            textTransform: "none",
                          }}
                          href={getExternalUrl(item.url)}
                          onClick={() => logLinkClick(item.url)}
                        >
                          Buy at {getUrl(item.url)}
                        </Link>
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              </Container>
            ) : (
              <Container
                sx={{
                  width: isDesktopOrLaptop ? "50%" : "100%",
                  minWidth: "500px",
                }}
              >
                <Stack spacing={2}>
                  <Typography
                    variant={isDesktopOrLaptop ? "h6" : "h5"}
                    sx={{
                      height: "20px",
                      paddingLeft: "5px",
                      marginTop: "20px",
                      fontFamily: HEADING_FONT,
                      fontWeight: 600,
                    }}
                  >
                    Retailers
                  </Typography>

                  <Divider />
                  <Stack spacing={0} alignItems="flex-end">
                    {mostRecentDate ? (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontFamily: HEADING_FONT,
                            fontSize: "12px",
                            color: getMutedText(darkMode),
                          }}
                        >
                          {`Last updated: ${momenttz(mostRecentDate)
                            .tz(
                              Intl.DateTimeFormat().resolvedOptions().timeZone
                            )
                            .format("ddd MMM DD YYYY @ hh:mm a zz")}`}
                        </Typography>
                      </Box>
                    ) : (
                      <></>
                    )}
                    {item.retailers.length > 0 ? (
                      <TableContainer
                        sx={{
                          marginTop: "8px",
                          fontSize: isDesktopOrLaptop ? "16px" : "24px",
                          borderRadius: "10px",
                          border: `1px solid ${getBorder(darkMode)}`,
                          backgroundImage: "none",
                          overflow: "hidden",
                        }}
                        component={Paper}
                        elevation={0}
                      >
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                align="center"
                                sx={getHeaderCellSx(darkMode)}
                              >
                                Retailer
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={getHeaderCellSx(darkMode)}
                              >
                                Shipping
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={getHeaderCellSx(darkMode)}
                              >
                                Price
                              </TableCell>
                              <TableCell
                                sx={getHeaderCellSx(darkMode)}
                              ></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {!getAffiliateKey(item.url) ||
                            item.retailers.find(
                              ({ retailer }) =>
                                retailer.toLowerCase() ===
                                getAffiliateKey(item.url)
                            ) ? (
                              <></>
                            ) : (
                              <TableRow>
                                <TableCell align="center">
                                  <BrandBuyButton
                                    retailer={getAffiliateKey(item.url)}
                                    url={item.url}
                                    darkMode={darkMode}
                                  />
                                </TableCell>
                                <TableCell align="center">-</TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontSize: isDesktopOrLaptop
                                      ? "16px"
                                      : "20px",
                                    fontFamily: HEADING_FONT,
                                    fontWeight: 600,
                                    fontVariantNumeric: "tabular-nums",
                                    color: "orange",
                                  }}
                                >
                                  <Tooltip title="This price may be out of date as we do not capture live pricing data for this item for this retailer">
                                    <Typography>
                                      {Number(item.price) ? (
                                        <Link
                                          target="_blank"
                                          component={Button}
                                          href={getExternalUrl(item.url)}
                                          onClick={() => logLinkClick(item.url)}
                                          sx={{
                                            textDecoration: "none",
                                            color: "orange",
                                            "&:hover": {
                                              cursor: "pointer",
                                            },
                                          }}
                                        >
                                          {Number(item.price).toLocaleString(
                                            "en-US",
                                            {
                                              style: "currency",
                                              currency: "USD",
                                            }
                                          )}
                                        </Link>
                                      ) : (
                                        "-"
                                      )}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Link
                                    target="_blank"
                                    component={Button}
                                    variant="contained"
                                    size="large"
                                    style={{
                                      backgroundColor: "#66bb6a",
                                      color: "white",
                                      borderRadius: "9px",
                                      fontFamily: HEADING_FONT,
                                      fontWeight: 600,
                                      textTransform: "none",
}}
                                    href={getExternalUrl(item.url)}
                                    onClick={() => logLinkClick(item.url)}
                                  >
                                    Buy
                                  </Link>
                                </TableCell>
                              </TableRow>
                            )}
                            {item.retailers.map(
                              ({
                                retailer,
                                buyLink,
                                salePrice,
                                retailPrice,
                              }) => (
                                <TableRow>
                                  <TableCell align="center">
                                    <BrandBuyButton
                                      retailer={retailer}
                                      url={buyLink}
                                      product={item}
                                      darkMode={darkMode}
                                    />
                                  </TableCell>
                                  <TableCell align="center">-</TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: isDesktopOrLaptop
                                        ? "16px"
                                        : "20px",
                                      fontFamily: HEADING_FONT,
                                      fontWeight: 600,
                                      fontVariantNumeric: "tabular-nums",
                                    }}
                                  >
                                    {Number(salePrice) ? (
                                      <Link
                                        target="_blank"
                                        component={Button}
                                        href={getExternalUrl(
                                          buyLink,
                                          false,
                                          item
                                        )}
                                        onClick={() => logLinkClick(buyLink)}
                                        sx={{
                                          textDecoration: "none",
                                          "&:hover": {
                                            cursor: "pointer",
                                          },
                                        }}
                                      >
                                        <Stack>
                                          {Number(retailPrice) &&
                                          Number(retailPrice) !==
                                            Number(salePrice) ? (
                                            <Typography
                                              variant="subtitle2"
                                              sx={{
                                                textDecoration: "line-through",
                                                color: "grey",
                                              }}
                                            >
                                              {Number(
                                                retailPrice
                                              ).toLocaleString("en-US", {
                                                style: "currency",
                                                currency: "USD",
                                              })}
                                            </Typography>
                                          ) : (
                                            <></>
                                          )}
                                          {Number(salePrice).toLocaleString(
                                            "en-US",
                                            {
                                              style: "currency",
                                              currency: "USD",
                                            }
                                          )}
                                        </Stack>
                                      </Link>
                                    ) : (
                                      "-"
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Link
                                      target="_blank"
                                      component={Button}
                                      underline="none"
                                      size="large"
                                      style={{
                                        backgroundColor: "#66bb6a",
                                        color: "white",
                                        borderRadius: "9px",
                                        fontFamily: HEADING_FONT,
                                        fontWeight: 600,
                                        textTransform: "none",
}}
                                      href={getExternalUrl(
                                        buyLink,
                                        false,
                                        item
                                      )}
                                      onClick={() => logLinkClick(buyLink)}
                                    >
                                      Buy
                                    </Link>
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography
                        textAlign="center"
                        sx={{
                          marginTop: "10px",
                          fontFamily: HEADING_FONT,
                          color: getMutedText(darkMode),
                        }}
                      >
                        No Retailers Found
                      </Typography>
                    )}
                  </Stack>
                  <Typography
                    variant={isDesktopOrLaptop ? "h6" : "h5"}
                    sx={{
                      height: "20px",
                      paddingLeft: "5px",
                      marginTop: "20px",
                      fontFamily: HEADING_FONT,
                      fontWeight: 600,
                    }}
                  >
                    Price History
                  </Typography>
                  <Divider sx={{ paddingTop: "15px" }} />
                  {item.retailers.length > 0 ? (
                    <PriceHistory
                      retailers={item.retailers}
                      darkMode={darkMode}
                    />
                  ) : (
                    <Typography
                        textAlign="center"
                        sx={{
                          marginTop: "10px",
                          fontFamily: HEADING_FONT,
                          color: getMutedText(darkMode),
                        }}
                      >
                      No Pricing History Available
                    </Typography>
                  )}
                </Stack>
              </Container>
            )}
          </Stack>
          <GearAnalyticsContainer
            gearType={gearType}
            productId={id}
            gearText={gearText}
            darkMode={darkMode}
          />
          <ReviewSystemContainer productId={id} gearType={gearType} />
          <GearDealsContainer gearType={gearType} darkMode={darkMode} />
        </Stack>
      </Stack>
      <Snackbar
        open={snackbarIsOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "300px", color: "white" }}
        >
          Copied to clipboard
        </Alert>
      </Snackbar>
      <Snackbar
        open={reportSnackBarIsOpen}
        autoHideDuration={3000}
        onClose={handleReportSnackBarClose}
      >
        <Alert
          onClose={handleReportSnackBarClose}
          severity="warning"
          sx={{ width: "300px", color: "white" }}
        >
          Thank you for reporting!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ItemDisplay;
